import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getRateLimitLogs } from '../api';
import {
  Paper, Box, Button, Chip, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';

export default function RateLimitLogs() {
  const theme = useTheme();
  const [endDate, setEndDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(13, 'day'));
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRpp] = useState(50);

  const params = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    limit: rowsPerPage,
    offset: page * rowsPerPage
  }), [startDate, endDate, page, rowsPerPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getRateLimitLogs(params);
      setRows(res?.data || []);
      setMeta(res?.meta || null);
    } catch (e) {
      console.error(e);
      setError('Failed to load rate limit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchData(); 
    // eslint-disable-next-line
  }, []);
  
  useEffect(() => { 
    fetchData(); 
  }, [startDate, endDate, page, rowsPerPage]);

  const total = meta?.total ?? rows.length;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRpp(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = ['IP Address', 'User ID', 'Timestamp', 'Message'];

  if (loading && !rows.length) return <LoadingBlock height={320} />;
  if (error && !rows.length) return <ErrorBlock message={error} onRetry={fetchData} />;

  return (
    <>
      <PageHeader title="Rate Limit Logs" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <DatePicker 
            label="Start" 
            value={startDate} 
            onChange={(d) => d && setStartDate(d)} 
            slotProps={{ textField: { size: 'small' } }} 
          />
          <DatePicker 
            label="End" 
            value={endDate} 
            onChange={(d) => d && setEndDate(d)} 
            slotProps={{ textField: { size: 'small' } }} 
          />
          <Button variant="outlined" onClick={() => { setPage(0); fetchData(); }}>
            Apply
          </Button>
          {meta && (
            <Chip 
              variant="outlined" 
              label={`Window: ${meta.start?.slice(0, 10)} → ${meta.end?.slice(0, 10)} • ${total} events`} 
            />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            No rate limit events found in the selected date range.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c} sx={{ fontWeight: 'bold' }}>
                      {c}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const readableDate = r.timestamp ? new Date(r.timestamp).toLocaleString() : '';
                  return (
                    <TableRow 
                      key={r.id} 
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell>{r.ipAddress || 'N/A'}</TableCell>
                      <TableCell>
                        {r.userId ? (
                          <Link
                            to={`/user/${r.userId}`}
                            state={{ from: { label: 'Rate Limit Logs', path: '/rate-limit-logs' } }}
                            style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 600 }}
                          >
                            {r.username || `User #${r.userId}`}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{readableDate}</TableCell>
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>{r.message || ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </>
        )}
      </Paper>
    </>
  );
}

